import { PageSizing } from '__components/Layout/PageContentWidth';
import {
  StyledClipBoardButton,
  StyledRowPlaceholder,
  StyledTwoColumnsRow,
  StyledWebhookLabel,
} from './styles';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect } from 'react';
import { SmartConnectorContext } from '../../context';
import type {
  GenericOption,
  SmartConnectorActivityField,
  SmartConnectorBulkActionField,
  SmartConnectorType,
} from 'pages/SmartConnectors/types';
import { SmartConnectorTypeButtons } from './SmartConnectorTypeButtons';
import { CustomObjectSelect } from './CustomObjectSelect';
import { NameInput } from './NameInput';
import { EmailInput } from './EmailInput';
import { StyledCard, StyledLoader, StyledStepHeader } from '../styles';
import {
  SMART_CONNECTOR_STEPS,
  SMART_CONNECTOR_TYPES,
} from 'pages/SmartConnectors/constants';
import { DiffCheckingSettings } from './DiffCheckingSettings';
import { ApiNameInput } from 'pages/SmartConnectors/components/ApiNameInput';
import { WithMobileWarning } from 'pages/SmartConnectors/components/WithMobileWarning';
import { MaxConcurrentExecutionsInput } from './MaxConcurrentExecutionsInput';
import TextInput from '__components/Inputs/TextInput';
import { CadenceSelect } from './CadenceSelect';
import { DescriptionInput } from './DescriptionInput';
import { Schedule } from './Schedule';
import { RRuleSchedulerState } from 'ts-components/AutomationEngine/Scheduler';
import { BulkactionFieldsSelect } from './BulkactionFieldsSelect';
import { ActivityFieldsSelect } from './ActivityFieldSelect';
import { ActivitySelect } from './ActivitySelect';

export const GeneralSettingsStep = () => {
  const { t } = useTranslation();
  const { smartConnector, stepData, setStepData, setIsValid, loading } =
    useContext(SmartConnectorContext);

  useEffect(() => {
    setIsValid(true);
  }, [setIsValid]);

  const handleChangeName = (value: string) => {
    setStepData((prev) => ({ ...prev, name: value }));
  };

  const handleChangeDescription = (value: string) => {
    setStepData((prev) => ({ ...prev, description: value }));
  };

  const handleChangeErrorEmail = (value: string) => {
    setStepData((prev) => ({ ...prev, error_notification_email: value }));
  };

  const handleChangeAllEmail = (value: string) => {
    setStepData((prev) => ({ ...prev, all_notification_email: value }));
  };

  const handleChangeMaxConcurrentExecutions = (value: number) => {
    setStepData((prev) => ({ ...prev, max_concurrent_executions: value }));
  };

  const handleChangeConnectorType = ({
    value,
  }: {
    value: SmartConnectorType;
  }) => {
    setStepData((prev) => ({ ...prev, connector_type: value }));
  };

  const handleChangeObject = (value: string) => {
    setStepData((prev) => ({ ...prev, custom_object: value }));
  };

  const handleChangeActivity = ({ value, label }: GenericOption) => {
    if (value !== stepData.activity_object?.id) {
      setStepData((prev) => ({
        ...prev,
        activity_object: { id: value, name: label },
        activity_fields: [],
      }));
    }
  };

  const handleChangeCadence = (value: number) => {
    setStepData((prev) => ({ ...prev, cadence: value }));
  };

  const handleChangeScheduleRRule = (value: RRuleSchedulerState) => {
    setStepData((prev) => ({
      ...prev,
      schedule_rrule: value.rrule,
      schedule_is_advanced: value.isAdvanced,
    }));
  };

  const handleChangeBulkactionFields = (
    value: SmartConnectorBulkActionField[]
  ) => {
    setStepData((prev) => ({
      ...prev,
      bulkaction_fields: value,
    }));
  };

  const handleChangeActivityFields = (value: SmartConnectorActivityField[]) => {
    setStepData((prev) => ({
      ...prev,
      activity_fields: value,
    }));
  };

  const webhookURL = `${import.meta.env.VITE_API_BASE_PATH}/smart-connectors/${smartConnector.id}/webhook`;

  return (
    <WithMobileWarning>
      <PageSizing>
        <StyledCard data-qa={`${SMART_CONNECTOR_STEPS.general}-step`}>
          <StyledStepHeader type="subheader">
            {t('General Settings')}
          </StyledStepHeader>
          <StyledLoader loading={loading}>
            <StyledTwoColumnsRow>
              <NameInput value={stepData.name} onChange={handleChangeName} />
              <ApiNameInput value={stepData.api_name} />
            </StyledTwoColumnsRow>
            <DescriptionInput
              description={stepData?.description || ''}
              aiDescription={smartConnector?.ai_description}
              onChange={handleChangeDescription}
            />
            <SmartConnectorTypeButtons
              value={stepData.connector_type!}
              onChange={handleChangeConnectorType}
            />
            {smartConnector.connector_type === SMART_CONNECTOR_TYPES.webhook ? (
              <StyledTwoColumnsRow>
                <TextInput
                  value={webhookURL}
                  label={
                    <StyledWebhookLabel>
                      {t('Webhook URL')}
                      <StyledClipBoardButton value={webhookURL} />
                    </StyledWebhookLabel>
                  }
                  disabled
                  margin
                />
                <CadenceSelect
                  label={t('Process New Webhooks Every')}
                  labelInfo={t(
                    'Received webhooks will be accumulated for the selected amount of time and processed as a batch'
                  )}
                  value={stepData.cadence}
                  onChange={handleChangeCadence}
                />
              </StyledTwoColumnsRow>
            ) : null}
            {smartConnector.connector_type ===
            SMART_CONNECTOR_TYPES.activity ? (
              <StyledTwoColumnsRow>
                <ActivitySelect
                  value={stepData.activity_object!}
                  onChange={handleChangeActivity}
                  disabled={
                    !!smartConnector.id && !!smartConnector.activity_object
                  }
                />
                <CadenceSelect
                  label={t('Process Logged Activities Every')}
                  labelInfo={t(
                    'Logged activities will be accumulated for the selected amount of time and processed as a batch'
                  )}
                  value={stepData.cadence}
                  onChange={handleChangeCadence}
                />
              </StyledTwoColumnsRow>
            ) : null}
            {smartConnector.connector_type ===
            SMART_CONNECTOR_TYPES.schedule ? (
              <Schedule
                rrule={stepData.schedule_rrule}
                onChange={handleChangeScheduleRRule}
                isAdvanced={stepData.schedule_is_advanced}
              />
            ) : null}
            <StyledTwoColumnsRow>
              {smartConnector.connector_type ===
              SMART_CONNECTOR_TYPES.activity ? (
                <ActivityFieldsSelect
                  activityObjectId={stepData.activity_object?.id}
                  value={stepData.activity_fields}
                  onChange={handleChangeActivityFields}
                />
              ) : (
                <CustomObjectSelect
                  value={stepData.custom_object!}
                  onChange={handleChangeObject}
                  disabled={!!smartConnector.id}
                />
              )}
              <EmailInput
                name="all_notification_email"
                value={stepData.all_notification_email}
                label={t('Additional Notification Email')}
                labelInfo={t(
                  'Notification emails will always be sent to the uploading user by default, but you may also add another email recipient. This address will receive both Success and Error notifications.'
                )}
                onChange={handleChangeAllEmail}
              />
            </StyledTwoColumnsRow>
            <StyledTwoColumnsRow>
              {smartConnector.connector_type ===
              SMART_CONNECTOR_TYPES.bulkaction ? (
                <BulkactionFieldsSelect
                  value={stepData.bulkaction_fields}
                  onChange={handleChangeBulkactionFields}
                />
              ) : (
                <DiffCheckingSettings />
              )}
              <EmailInput
                name="error_notification_email"
                value={stepData.error_notification_email}
                label={t('Additional Error Notification Email')}
                labelInfo={t(
                  'Error emails will always be sent to the uploading user by default, but you may also add another email recipient (such as a distribution group).'
                )}
                onChange={handleChangeErrorEmail}
              />
            </StyledTwoColumnsRow>
            {smartConnector.connector_type ===
            SMART_CONNECTOR_TYPES.bulkaction ? (
              <StyledTwoColumnsRow>
                <DiffCheckingSettings />
                <StyledRowPlaceholder />
              </StyledTwoColumnsRow>
            ) : null}
            <StyledTwoColumnsRow>
              <MaxConcurrentExecutionsInput
                value={stepData.max_concurrent_executions}
                onChange={handleChangeMaxConcurrentExecutions}
              />
              <div />
            </StyledTwoColumnsRow>
          </StyledLoader>
        </StyledCard>
      </PageSizing>
    </WithMobileWarning>
  );
};
